<template>
	<article class="d-frontpage">
		<div
			class="d-frontpage__content w-full max-w-layout-max px-layout-margin"
		>
			<header
				v-if="title || name"
				class="mt-[10px] mb-[30px] >=568:mb-[60px]"
			>
				<h1
					class="text-25 >=568:text-45 p-[25px] text-center font-light"
					v-html="title || name"
				></h1>
			</header>

			<!-- FIRST ROW CARDS -->
			<div
				class="grid >=760:grid-cols-3 grid-cols-1 gap-[10px] w-full mb-[50px]"
			>
				<FrontpageCard
					color="cyan"
					:link="box1Link"
					:text="box1Text"
					:title="box1Title"
				>
					<template #icon>
						<SvgIconPaintbrush class="h-[65px] fill-white" />
					</template>
				</FrontpageCard>
				<FrontpageCard
					color="blue"
					:link="box2Link"
					:text="box2Text"
					:title="box2Title"
				>
					<template #icon>
						<SvgIconBook class="h-[55px] fill-white" />
					</template>
				</FrontpageCard>
				<FrontpageCard
					color="purple"
					:link="box3Link"
					:text="box3Text"
					:title="box3Title"
				>
					<template #icon>
						<SvgIconLock class="h-[50px] fill-white" />
					</template>
				</FrontpageCard>
			</div>
			<!-- SECOND ROW CARDS -->
			<div
				class="d-frontpage__second-row grid >=760:grid-cols-3 grid-cols-1 gap-[10px] w-full mb-[10px]"
			>
				<article v-if="searchForm" class="p-box-padding bg-cyan-dark">
					<h2
						v-if="searchForm.title"
						class="text-18 >=568:text-22 leading-none font-bold mb-[10px]"
					>
						{{ searchForm.title }}
					</h2>
					<FetchForm
						v-if="searchForm.endpointUrl"
						:action="searchForm.endpointUrl"
						:method="searchForm.method"
						use-native-form-data-on-post
						class="w-full"
						@response="onResponse"
						@error="onError"
					>
						<FilterText
							v-if="searchForm.fieldByName.code"
							v-model="searchForm.fieldByName.code.value"
							type="search"
							:name="searchForm.fieldByName.code.name"
							:placeholder="
								searchForm.fieldByName.code.placeholder
							"
							class="w-full"
						/>
					</FetchForm>
				</article>
				<article
					v-if="newsContainer"
					class="p-box-padding bg-orange-dark"
				>
					<div class="flex justify-between mb-[20px]">
						<h2
							v-if="newsContainer.newsTitle"
							class="text-18 >=568:text-22 leading-none font-bold"
						>
							{{ newsContainer.newsTitle }}
						</h2>
						<a
							v-if="newsContainer.newsAllLink"
							class="text-white text-12 underline decoration-white/[.4] hover:decoration-white/[.2] hover:text-opacity-80 transition-colors duration-300"
							:href="newsContainer.newsAllLink"
							>{{ newsContainer.newsAllTitle }}</a
						>
					</div>
					<div
						v-for="item in newsContainer.items"
						:key="item.id"
						class="font-medium mb-[25px]"
					>
						<a
							:href="item.url"
							class="text-white underline decoration-white/[.4] hover:decoration-white/[.2] hover:text-opacity-80 transition-colors duration-300"
							v-text="item.name"
						></a>
					</div>
				</article>
				<article
					v-if="box4Title"
					class="p-box-padding bg-white text-text"
				>
					<header
						class="flex flex-wrap gap-[10px] justify-between items-center mb-[10px]"
					>
						<h2
							class="text-18 >=568:text-22 leading-none font-bold"
						>
							{{ box4Title }}
						</h2>
						<a
							v-if="box4Link && box4Link.url"
							class="ml-auto text-text text-12 underline hover:text-opacity-80 transition-colors duration-300 flex justify-end"
							:href="box4Link.url"
						>
							{{ box4Link.name }}
						</a>
					</header>
					<p v-if="box4Text">{{ box4Text }}</p>
				</article>
			</div>
			<!-- THIRD ROW CARDS -->
			<div
				class="d-frontpage__third-row w-full mb-[80px] grid >=760:grid-cols-3 grid-cols-1 gap-[10px]"
			>
				<a
					v-if="smallBox1Link && smallBox1Link.url"
					:href="smallBox1Link.url"
					class="d-frontpage__third-row-item d-frontpage__third-row-item--red bg-red >=760:col-start-1 >=760:col-end-3 justify-between"
				>
					<div class="pr-[10px]">{{ smallBox1Title }}</div>
					<SvgIconWedge class="w-[13px] h-[13px] fill-white" />
				</a>
				<a
					v-if="smallBox2Link && smallBox2Link.url"
					:href="smallBox2Link.url"
					class="d-frontpage__third-row-item d-frontpage__third-row-item--green bg-green text-text justify-between"
				>
					<div class="pr-[10px]">{{ smallBox2Title }}</div>
					<SvgIconWedge class="w-[13px] h-[13px] fill-text" />
				</a>
			</div>
		</div>
	</article>
</template>

<script>
export default {
	inheritAttrs: false,
};
</script>

<script setup>
import { useLimboForm } from '~~/composables/useLimboForm';
import SvgIconWedge from '~/assets/svgs/icon-wedge.svg';
import SvgIconPaintbrush from '~/assets/svgs/icon-paintbrush.svg';
import SvgIconBook from '~/assets/svgs/icon-book.svg';
import SvgIconLock from '~/assets/svgs/icon-lock.svg';

const router = useRouter();

const props = defineProps({
	id: Number,
	title: String,
	name: String,
	header: Object,
	contentElements: Array,
	box1Link: Object,
	box1Text: String,
	box1Title: String,
	box2Link: Object,
	box2Text: String,
	box2Title: String,
	box3Link: Object,
	box3Text: String,
	box3Title: String,
	box4Link: Object,
	box4Text: String,
	box4Title: String,
	smallBox1Title: String,
	smallBox1Link: Object,
	smallBox2Title: String,
	smallBox2Link: Object,
	newsContainer: Object,
	forms: Array,
});

const searchForm = ref(
	props.forms?.length ? useLimboForm(props.forms?.[0]) : null
);

// Methods
const onError = () => {
	router.push(
		'/find-via-behandlingsnummer/?code=' +
			encodeURIComponent(searchForm.value?.fieldByName?.code?.value ?? '')
	);
};
const onResponse = ({ redirectUrl }) => {
	if (redirectUrl) {
		router.push(redirectUrl);
	} else {
		onError();
	}
};
</script>

<style lang="postcss">
.d-frontpage {
	@apply text-white flex justify-center;
}
.d-frontpage__third-row-item {
	transition: transform 0.3s;
	display: flex;
	align-items: center;
	padding: 25px;

	&--red {
		background-image: linear-gradient(#9e0b06, #b7312c);
		&:hover {
			transform: scale(1.025);
		}
	}
	&--green {
		background-image: linear-gradient(#cdd23f, #dce14d);
		&:hover {
			transform: scale(1.05);
		}
	}
}
</style>
